"use strict";
/**
 * Scan browser localStorage for local draft / working / typing / archive data
 * so the profile (My Account) panel can list recoverable note IDs.
 *
 * Does not touch pilot_notes — client-only inventory.
 *
 * Body preview priority (actual unsaved content, not scan order):
 *   live form (optional) > newest among working / typing / draft tip > archive
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.scanLocalDraftInventory = void 0;
var resultRowDraftStorage_1 = require("./resultRowDraftStorage");
var editWorkingStream_1 = require("./editWorkingStream");
var editTypingStream_1 = require("./editTypingStream");
function bodyPreview(text, max) {
    if (max === void 0) { max = 96; }
    var cleaned = (text || '').replace(/\r\n?/g, '\n').trim();
    if (!cleaned)
        return '(empty body)';
    // Join first non-empty lines so "title\\n\\nnew edit" is not reduced to title only.
    var lines = cleaned
        .split('\n')
        .map(function (l) { return l.trim().replace(/^[-*+]\s+/, ''); })
        .filter(function (l) { return l.length > 0; })
        .slice(0, 3);
    var joined = lines.join(' · ').replace(/\s+/g, ' ');
    return joined.length > max ? "".concat(joined.slice(0, max), "\u2026") : joined;
}
function maxIso(a, b) {
    if (!a)
        return b || null;
    if (!b)
        return a;
    return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}
function parseDraftKey(key) {
    // audroam.resultRowDraft:{person}:{mode}:{rowId}
    var prefix = resultRowDraftStorage_1.LOCAL_DRAFT_STORAGE_PREFIX + ':';
    if (!key.startsWith(prefix))
        return null;
    var rest = key.slice(prefix.length).split(':');
    if (rest.length < 3)
        return null;
    var personId = rest[0];
    var mode = rest[1];
    var rowId = rest.slice(2).join(':');
    if (mode !== 'edit' && mode !== 'reply')
        return null;
    return { personId: personId, mode: mode, rowId: rowId };
}
function parseWorkingOrTypingKey(key, prefix) {
    var p = prefix + ':';
    if (!key.startsWith(p))
        return null;
    var rest = key.slice(p.length).split(':');
    if (rest.length < 3)
        return null;
    var personId = rest[0];
    var mode = rest[1];
    var rowId = rest.slice(2).join(':');
    if (mode !== 'edit' && mode !== 'reply')
        return null;
    return { personId: personId, mode: mode, rowId: rowId };
}
/** Tie-break when timestamps equal or missing: form > working > typing > draft > archive */
var SOURCE_RANK = {
    form: 5,
    working: 4,
    typing: 3,
    draft: 2,
    archive: 1,
};
function pickBestBody(candidates) {
    var best = null;
    for (var _i = 0, candidates_1 = candidates; _i < candidates_1.length; _i++) {
        var c = candidates_1[_i];
        if (!c || c.body == null)
            continue;
        // Allow empty body as a valid candidate only if nothing else exists yet
        if (!best) {
            best = c;
            continue;
        }
        var tb = c.at ? Date.parse(c.at) : NaN;
        var ta = best.at ? Date.parse(best.at) : NaN;
        var tbOk = isFinite(tb);
        var taOk = isFinite(ta);
        if (tbOk && taOk) {
            if (tb > ta) {
                best = c;
                continue;
            }
            if (tb < ta)
                continue;
            // equal time → rank
            if (SOURCE_RANK[c.source] > SOURCE_RANK[best.source])
                best = c;
            continue;
        }
        if (tbOk && !taOk) {
            best = c;
            continue;
        }
        if (!tbOk && taOk)
            continue;
        // neither has time — prefer higher rank; if same rank prefer longer body
        if (SOURCE_RANK[c.source] > SOURCE_RANK[best.source]) {
            best = c;
        }
        else if (SOURCE_RANK[c.source] === SOURCE_RANK[best.source] &&
            c.body.length > best.body.length) {
            best = c;
        }
    }
    return best;
}
function accKey(personId, mode, rowId) {
    return "".concat(personId, "|").concat(mode, "|").concat(rowId);
}
function ensureAcc(map, personId, mode, rowId) {
    var k = accKey(personId, mode, rowId);
    var a = map.get(k);
    if (!a) {
        a = {
            rowId: rowId,
            mode: mode,
            personId: personId,
            hasDraft: false,
            hasWorking: false,
            hasTyping: false,
            archiveCount: 0,
            draftRevisionCount: 0,
            typingTickCount: 0,
            updatedAt: null,
            candidates: [],
            sources: new Set(),
        };
        map.set(k, a);
    }
    return a;
}
function personMatches(keyPerson, filterPersonId) {
    if (filterPersonId === undefined || filterPersonId === null || filterPersonId === '') {
        return true;
    }
    var f = String(filterPersonId);
    return keyPerson === f || keyPerson === 'anon';
}
/**
 * Scan localStorage for all local recovery data for notes.
 * @param filterPersonId Prefer current user; also includes `anon` keys.
 * @param liveSessions Optional open editors — form body wins when present.
 */
function scanLocalDraftInventory(storage, filterPersonId, liveSessions) {
    var _a, _b, _c, _d, _e, _f;
    if (!storage)
        return [];
    var map = new Map();
    var keys = [];
    try {
        for (var i = 0; i < storage.length; i++) {
            var k = storage.key(i);
            if (k)
                keys.push(k);
        }
    }
    catch (_g) {
        return [];
    }
    for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
        var key = keys_1[_i];
        // Active draft histories
        var dk = parseDraftKey(key);
        if (dk && personMatches(dk.personId, filterPersonId)) {
            try {
                var raw = storage.getItem(key);
                if (!raw)
                    continue;
                var hist = JSON.parse(raw);
                if (!((_a = hist === null || hist === void 0 ? void 0 : hist.revisions) === null || _a === void 0 ? void 0 : _a.length))
                    continue;
                var a = ensureAcc(map, dk.personId, dk.mode, dk.rowId);
                a.hasDraft = true;
                a.draftRevisionCount = hist.revisions.length;
                a.sources.add('draft');
                var latest = hist.revisions[0];
                a.updatedAt = maxIso(a.updatedAt, latest.savedAt);
                var body = ((_b = latest.fields) === null || _b === void 0 ? void 0 : _b.pnBody) != null ? String(latest.fields.pnBody) : '';
                a.candidates.push({ body: body, at: latest.savedAt || null, source: 'draft' });
            }
            catch (_h) {
                /* skip bad key */
            }
            continue;
        }
        // Working stream
        var wk = parseWorkingOrTypingKey(key, editWorkingStream_1.EDIT_WORKING_PREFIX);
        if (wk && personMatches(wk.personId, filterPersonId)) {
            try {
                var raw = storage.getItem(key);
                if (!raw)
                    continue;
                var snap = JSON.parse(raw);
                if (!snap || typeof snap.body !== 'string')
                    continue;
                var a = ensureAcc(map, wk.personId, wk.mode, wk.rowId);
                a.hasWorking = true;
                a.sources.add('working');
                a.updatedAt = maxIso(a.updatedAt, snap.updatedAt);
                a.candidates.push({
                    body: snap.body,
                    at: snap.updatedAt || null,
                    source: 'working',
                });
            }
            catch (_j) {
                /* skip */
            }
            continue;
        }
        // Typing stream
        var tk = parseWorkingOrTypingKey(key, editTypingStream_1.EDIT_TYPING_PREFIX);
        if (tk && personMatches(tk.personId, filterPersonId)) {
            try {
                var raw = storage.getItem(key);
                if (!raw)
                    continue;
                var log = JSON.parse(raw);
                if (!log || typeof log.latest !== 'string')
                    continue;
                var a = ensureAcc(map, tk.personId, tk.mode, tk.rowId);
                a.hasTyping = true;
                a.typingTickCount = Array.isArray(log.ticks) ? log.ticks.length : 0;
                a.sources.add('typing');
                a.updatedAt = maxIso(a.updatedAt, log.updatedAt);
                a.candidates.push({
                    body: log.latest,
                    at: log.updatedAt || null,
                    source: 'typing',
                });
            }
            catch (_k) {
                /* skip */
            }
            continue;
        }
        // Archives: one key per person with array of records
        if (key.startsWith(resultRowDraftStorage_1.LOCAL_DRAFT_ARCHIVE_PREFIX + ':')) {
            var personId = key.slice((resultRowDraftStorage_1.LOCAL_DRAFT_ARCHIVE_PREFIX + ':').length);
            if (!personMatches(personId, filterPersonId))
                continue;
            try {
                var raw = storage.getItem(key);
                if (!raw)
                    continue;
                var records = JSON.parse(raw);
                if (!Array.isArray(records))
                    continue;
                for (var _l = 0, records_1 = records; _l < records_1.length; _l++) {
                    var rec = records_1[_l];
                    if (!rec || rec.rowId === undefined)
                        continue;
                    var mode = (rec.mode === 'reply' ? 'reply' : 'edit');
                    var a = ensureAcc(map, personId, mode, String(rec.rowId));
                    a.archiveCount += 1;
                    a.sources.add('archive');
                    a.updatedAt = maxIso(a.updatedAt, rec.archivedAt || rec.latestSavedAt || null);
                    var archBody = ((_f = (_e = (_d = (_c = rec.history) === null || _c === void 0 ? void 0 : _c.revisions) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.fields) === null || _f === void 0 ? void 0 : _f.pnBody) != null
                        ? String(rec.history.revisions[0].fields.pnBody)
                        : '';
                    if (archBody) {
                        a.candidates.push({
                            body: archBody,
                            at: rec.archivedAt || rec.latestSavedAt || null,
                            source: 'archive',
                        });
                    }
                }
            }
            catch (_m) {
                /* skip */
            }
        }
    }
    // Live open editors — form is the true unsaved state while editing.
    if (liveSessions && liveSessions.length) {
        for (var _o = 0, liveSessions_1 = liveSessions; _o < liveSessions_1.length; _o++) {
            var live = liveSessions_1[_o];
            if (live.rowId === undefined || live.rowId === null)
                continue;
            var personId = live.personId != null && live.personId !== ''
                ? String(live.personId)
                : filterPersonId != null
                    ? String(filterPersonId)
                    : 'anon';
            if (!personMatches(personId, filterPersonId) && personId !== 'anon') {
                // still allow if map already has this row under filter person
            }
            var mode = live.mode === 'reply' ? 'reply' : 'edit';
            var rowId = String(live.rowId);
            // Prefer matching existing acc for this person filter
            var a = map.get(accKey(personId, mode, rowId)) ||
                (filterPersonId != null
                    ? map.get(accKey(String(filterPersonId), mode, rowId))
                    : undefined);
            if (!a) {
                a = ensureAcc(map, personId, mode, rowId);
            }
            var body = live.body == null ? '' : String(live.body);
            var at = live.updatedAt || new Date().toISOString();
            a.sources.add('form');
            a.updatedAt = maxIso(a.updatedAt, at);
            a.candidates.push({ body: body, at: at, source: 'form' });
        }
    }
    var items = [];
    map.forEach(function (a) {
        var _a, _b;
        var best = pickBestBody(a.candidates);
        var bodyFull = (_a = best === null || best === void 0 ? void 0 : best.body) !== null && _a !== void 0 ? _a : '';
        items.push({
            rowId: a.rowId,
            mode: a.mode,
            personId: a.personId,
            hasDraft: a.hasDraft,
            hasWorking: a.hasWorking,
            hasTyping: a.hasTyping,
            archiveCount: a.archiveCount,
            draftRevisionCount: a.draftRevisionCount,
            typingTickCount: a.typingTickCount,
            updatedAt: a.updatedAt,
            bodyPreview: bodyPreview(bodyFull),
            bodyFull: bodyFull,
            bodySource: (_b = best === null || best === void 0 ? void 0 : best.source) !== null && _b !== void 0 ? _b : null,
            sources: Array.from(a.sources),
        });
    });
    items.sort(function (x, y) {
        var tx = x.updatedAt ? new Date(x.updatedAt).getTime() : 0;
        var ty = y.updatedAt ? new Date(y.updatedAt).getTime() : 0;
        return (isFinite(ty) ? ty : 0) - (isFinite(tx) ? tx : 0);
    });
    return items;
}
exports.scanLocalDraftInventory = scanLocalDraftInventory;
